import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { Copy, Check } from 'lucide-react';
import 'highlight.js/styles/github-dark.css';

export default function MarkdownRenderer({ content, className = '' }) {
  const [copiedCode, setCopiedCode] = useState(null);

  const handleCopyCode = async (code, index) => {
    try {
      await navigator?.clipboard?.writeText(code);
      setCopiedCode(index);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error('Failed to copy code:', error);
    }
  };

  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight, rehypeRaw]}
        components={{
          h1: ({ node, ...props }) => (
            <h1 className="text-2xl font-bold mt-6 mb-4 text-gray-900" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 className="text-xl font-bold mt-5 mb-3 text-gray-900" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className="text-lg font-semibold mt-4 mb-2 text-gray-900" {...props} />
          ),
          h4: ({ node, ...props }) => (
            <h4 className="text-base font-semibold mt-3 mb-2 text-gray-900" {...props} />
          ),
          h5: ({ node, ...props }) => (
            <h5 className="text-sm font-semibold mt-3 mb-2 text-gray-900" {...props} />
          ),
          h6: ({ node, ...props }) => (
            <h6 className="text-sm font-semibold mt-2 mb-1 text-gray-900" {...props} />
          ),
          
          p: ({ node, ...props }) => (
            <p className="mb-4 leading-relaxed text-gray-800" {...props} />
          ),
          
          ul: ({ node, ...props }) => (
            <ul className="list-disc list-inside mb-4 space-y-2 text-gray-800" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-800" {...props} />
          ),
          li: ({ node, ...props }) => (
            <li className="ml-4" {...props} />
          ),
          
          blockquote: ({ node, ...props }) => (
            <blockquote 
              className="border-l-4 border-blue-500 pl-4 py-2 my-4 bg-blue-50 text-gray-700 italic"
              {...props} 
            />
          ),
          
          code: ({ node, inline, className, children, ...props }) => {
            const match = /language-(\w+)/?.exec(className || '');
            const language = match ? match?.[1] : '';
            const codeString = String(children)?.replace(/\n$/, '');
            const codeIndex = `${language}-${codeString?.slice(0, 20)}`;
            
            if (inline) {
              return (
                <code 
                  className="bg-gray-200 text-red-600 px-1.5 py-0.5 rounded text-sm font-mono"
                  {...props}
                >
                  {children}
                </code>
              );
            }
            
            return (
              <div className="relative group my-4">
                <div className="flex items-center justify-between bg-gray-800 text-gray-300 px-4 py-2 rounded-t-lg">
                  <span className="text-xs font-semibold uppercase">{language || 'code'}</span>
                  <button
                    onClick={() => handleCopyCode(codeString, codeIndex)}
                    className="flex items-center gap-1.5 px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs transition-colors"
                    title="Copiar código"
                  >
                    {copiedCode === codeIndex ? (
                      <>
                        <Check size={14} />
                        <span>Copiado!</span>
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        <span>Copiar</span>
                      </>
                    )}
                  </button>
                </div>
                <pre className="!mt-0 !rounded-t-none overflow-x-auto">
                  <code className={className} {...props}>
                    {children}
                  </code>
                </pre>
              </div>
            );
          },
          
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-4">
              <table 
                className="min-w-full divide-y divide-gray-300 border border-gray-300 rounded-lg"
                {...props} 
              />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead className="bg-gray-100" {...props} />
          ),
          tbody: ({ node, ...props }) => (
            <tbody className="bg-white divide-y divide-gray-200" {...props} />
          ),
          tr: ({ node, ...props }) => (
            <tr className="hover:bg-gray-50 transition-colors" {...props} />
          ),
          th: ({ node, ...props }) => (
            <th 
              className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
              {...props} 
            />
          ),
          td: ({ node, ...props }) => (
            <td 
              className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap"
              {...props} 
            />
          ),
          
          a: ({ node, ...props }) => (
            <a 
              className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
              target="_blank"
              rel="noopener noreferrer"
              {...props} 
            />
          ),
          
          hr: ({ node, ...props }) => (
            <hr className="my-6 border-t-2 border-gray-300" {...props} />
          ),
          
          strong: ({ node, ...props }) => (
            <strong className="font-bold text-gray-900" {...props} />
          ),
          
          em: ({ node, ...props }) => (
            <em className="italic text-gray-800" {...props} />
          ),
          
          del: ({ node, ...props }) => (
            <del className="line-through text-gray-600" {...props} />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}